import { useEffect, useRef, useState } from 'react';

import { useAtom, useSetAtom } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { MakeModelAtom } from '@core/Entities/Search/Atoms/MakeAtom.entity';
import type { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { ChevronDown } from '@gds/Icons/Paths/ChevronDown';
import { makeAtom } from '@search/Atoms/MakeAtom';
import { modelAtom } from '@search/Atoms/ModelAtom';

import type { MakeObject, MakeProps } from '../../Search.entity';
import { updateFilters } from '../../Utils/UpdateFilters';
import CustomDropDown from '../CustomDropDown/CustomDropDown';

import styles from '../../Search.module.css';

const Make = ({
  makePlaceholder,
  modelPlaceholder,
  searchPlaceholder,
  noResultText,
  makeTitle,
  state,
  updateFn,
  defaultCount,
  isReviewsPage,
  reviewItems,
}: MakeProps) => {
  const DEFAULT_OPTION = {
    key: 'any_make',
    displayName: makePlaceholder,
    count: defaultCount,
    make: 'anymake',
  };
  const makeRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredMakes, setFilteredMakes] = useState<MakeModelAtom[]>([]);

  const [selectedMake, setSelectedMake] = useAtom(makeAtom);
  const setFilters = useSetAtom(filtersListAtom);

  const [_, setSelectedModel] = useAtom(modelAtom);

  const handleMenuOpen = () => {
    setIsOpen(isOpen => !isOpen);
    setSearchKeyword('');
  };

  const eligibleMakes = isReviewsPage
    ? (reviewItems as MakeObject[])
    : (state.makes as MakeModelAtom[]);

  useEffect(() => {
    if (searchKeyword) {
      const filteredArr = eligibleMakes?.filter(data => {
        return (
          data.displayName.toLowerCase().includes(searchKeyword) ||
          data.make.toLowerCase().includes(searchKeyword)
        );
      });
      setFilteredMakes(searchKeyword ? filteredArr : []);
    }
  }, [searchKeyword]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!makeRef?.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
  }, [makeRef]);

  const handleSearchChange = (value: string) => {
    setSearchKeyword(value.toLowerCase());
  };

  const handleSelect = (option: MakeModelAtom) => {
    if (option) {
      setSelectedMake(option);
      updateFn({ ...state, totalCount: option.count });
      setSelectedModel({
        key: 'any_model',
        displayName: modelPlaceholder,
        count: option.count,
        make: 'anymodel',
      });
      if (!isReviewsPage) {
        const filterUpdates: Filter[] = [
          {
            id: 'make',
            value: option.make === 'anymake' ? undefined : [option.make],
          },
          { id: 'model', value: undefined },
          { id: 'monthly-price__lte', value: undefined },
          { id: 'price__lte', value: undefined },
        ];
        setFilters(prev => updateFilters(prev, filterUpdates));
      }
      setIsOpen(false);
    }
  };

  return (
    <div
      className={`${styles.makeModelWrapper} ${isOpen ? styles.active : ''}`}
      ref={makeRef}
      data-test-id="make-component"
      aria-label="make-value"
    >
      <div
        className={`${styles.dropdownWrapper} ${styles.first}`}
        onClick={handleMenuOpen}
        data-test-id="make-menu-button"
      >
        <span className={styles.makeValue} data-test-id="make-value">
          {selectedMake?.key === 'any_make' ? makePlaceholder : selectedMake?.displayName}
        </span>
        <div className={styles.iconWrapper}>
          <ChevronDown className={isOpen ? styles.rotate : ''} />
        </div>
      </div>
      {isOpen && (
        <CustomDropDown
          items={
            searchKeyword.length > 0 ? filteredMakes : [DEFAULT_OPTION, ...(eligibleMakes ?? [])]
          }
          isGrouped
          onSelect={handleSelect}
          checkedValue={selectedMake?.make}
          dataTestId="make-menu"
          searchPlaceholder={searchPlaceholder}
          handleSearchChange={handleSearchChange}
          noResultText={noResultText}
          title={makeTitle}
          handleMenuOpen={handleMenuOpen}
          countLabel={!isReviewsPage}
        />
      )}
    </div>
  );
};

export default Make;
